import React, { useState, useRef, useEffect, forwardRef, useMemo } from 'react';
import { sanitize } from 'config/xssConfig';
import { Clock, Send } from 'lucide-react';
import TextEditor from 'components/TextEditor';
import type { MediaSlot } from 'hooks/useMediaManager';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';
import { useUser } from 'util/UserContext';
import { transformHtmlContent } from 'util/transformHtmlContent';
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import EntityAvatar from 'components/Entities/EntityAvatar';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import { loadCoauthorData, handleCoauthorSelect as selectCoauthor, handleCoauthorRemove as removeCoauthor } from 'util/entityUtils';
import DetailPanel from 'ui/DetailPanel';
import AppSettingsService from 'services/appSettingsService';
import type { PollDraft } from 'types/poll';
import { useAutoSaveDraft } from 'hooks/useAutoSaveDraft';
import TimelinePlaceInput from 'components/Comments/TimelinePlaceInput';
import type { PlaceRecord } from 'lib/api/places';
import DraftSaveDialog from 'ui/DraftSaveDialog';
import { getDraftUiStrings, resolveDraftLocale } from 'i18n/draftUi';
/**
 * Props for the CommentInput component
 */
export interface CommentInputContext {
  place: PlaceRecord | null;
  placeId: number | null;
  timelineTs: string | null;
}

/**
 * Imperative handle exposed to parents that use `hideFooter` to embed
 * the composer inside their own form chrome.
 */
export interface CommentInputSubmitHandle {
  /** Trigger the same validation + submit pipeline the footer button uses. */
  submit: () => void;
  /** Clear every transient composer state (content, privacy, coauthors, draft). */
  reset: () => void;
  /** Read the current trimmed content without triggering a submit. */
  getContent: () => string;
}

/**
 * Text-editor + footer change when the composer is embedded inside a
 * managed list (e.g. a solution's principles section) vs. a free-
 * standing comment thread. Default behaviour (`action='add'|'edit'|
 * 'reply'|'rebut'|'form'` with their existing chrome) is unchanged; the
 * new props below are all opt-in.
 */
interface CommentInputProps {
  onSubmit: (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    addedCoauthors: CoauthorSuggestion[],
    removedCoauthors: CoauthorSuggestion[],
    pollDraft?: PollDraft | null,
    context?: CommentInputContext | null
  ) => void;
  onCancel?: () => void;
  onSaveDraft?: (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    pollDraft?: PollDraft | null,
    context?: CommentInputContext | null
  ) => Promise<void> | void;
  buttonText: string;
  initialContent?: string;
  initialPrivacy?: PrivacyLevel;
  action?: 'add' | 'reply' | 'edit' | 'form' | 'rebut';
  commentData?: {
    id: number;
    user_id?: number | undefined;
    username: string;
    content?: string;
    updated_at?: string;
    created_at?: string;
    /** Canonical comment-kind. Use `'argument'` for teabate-argument rows. */
    kind?: string;
    properties?: {
      coauthors?: CoauthorSuggestion[];
      argument_type?: 'agree' | 'disagree';
      level?: number;
      media_files?: any[];
    };
  };
  onCoauthorsChange?: (addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void;
  onCanSubmitChange?: (canSubmit: boolean) => void;
  /** Emits TipTap HTML on every content change. Useful for controlled embeds. */
  onContentChange?: (html: string) => void;
  onMediaSelected?: (files: FileList | null) => void;
  onMediaManagerChange?: (media: import('hooks/useMediaManager').MediaSlot[]) => void;
  onEditMediaStateChange?: (state: any) => void;
  draftKey?: string | null;
  draftEnabled?: boolean;
  initialTimelineTs?: string | null;
  initialPlace?: PlaceRecord | null;
  /** Legacy convenience flag; equivalent to `showTimeline && showPlace`. */
  showTimelinePlace?: boolean;
  /** Show the timeline chip specifically. Defaults follow `showTimelinePlace`. */
  showTimeline?: boolean;
  /** Show the place chip specifically. Defaults follow `showTimelinePlace`. */
  showPlace?: boolean;
  /** Show the privacy selector (default true). Context composers hide this. */
  showPrivacy?: boolean;
  /**
   * When set, the privacy selector is disabled and the composer reports
   * this privacy value to `onSubmit`. Context composers pass the parent
   * post's privacy here so the comment inherits it.
   */
  forcedPrivacy?: PrivacyLevel | null;
  /** Show the coauthor picker + chip list (default true). */
  allowCoauthors?: boolean;
  /** Suppress the built-in submit/cancel footer (parent owns submission). */
  hideFooter?: boolean;
  /**
   * When the parent hides the footer, it can pass an imperative ref here
   * to trigger submit/reset from its own UI chrome. The ref is only
   * populated when `hideFooter` is true.
   */
  submitRef?: React.MutableRefObject<CommentInputSubmitHandle | null>;
  isMobile?: boolean;
  placeholder?: string;
  cancelText?: string;
  autoFocus?: boolean;
}

/**
 * CommentInput component for adding, replying to, or editing comments
 */
const CommentInput = forwardRef<HTMLDivElement, CommentInputProps>(({ 
  onSubmit,
  onCancel,
  onSaveDraft,
  buttonText,
  initialContent = '',
  initialPrivacy = 'public',
  action = 'add',
  commentData,
  onCoauthorsChange,
  onCanSubmitChange,
  onContentChange,
  onMediaSelected,
  onMediaManagerChange,
  onEditMediaStateChange,
  draftKey = null,
  draftEnabled = false,
  initialTimelineTs = null,
  initialPlace = null,
  showTimelinePlace = false,
  showTimeline,
  showPlace,
  showPrivacy = true,
  forcedPrivacy = null,
  allowCoauthors = true,
  hideFooter = false,
  submitRef,
  isMobile = false,
  placeholder,
  cancelText = "Cancel",
  autoFocus = false,
}, ref) => {
  // Resolve the finer-grained timeline/place visibility from the new props
  // while preserving the legacy `showTimelinePlace` flag as a fallback so
  // existing consumers (comment threads, edge notes) keep rendering the
  // combined chip bar exactly as before.
  const effectiveShowTimeline = typeof showTimeline === 'boolean' ? showTimeline : showTimelinePlace;
  const effectiveShowPlace = typeof showPlace === 'boolean' ? showPlace : showTimelinePlace;
  const effectiveShowTimelinePlace = effectiveShowTimeline || effectiveShowPlace;
  const [content, setContent] = useState(initialContent);
  const { currentUser, awaitingUser } = useUser();
  const [privacy, setPrivacy] = useState<PrivacyLevel>(
    forcedPrivacy || initialPrivacy || currentUser?.properties?.privacy_content_default_audience?.value || 'public'
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState<number>(0);
  const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [existingCoauthors, setExistingCoauthors] = useState<CoauthorSuggestion[]>(commentData?.properties?.coauthors || []);
  const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [pendingCoauthors, setPendingCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [mediaFiles, setMediaFiles] = useState<FileList | null>(null);
  const [isLoadingCoauthors, setIsLoadingCoauthors] = useState<boolean>(false);
  const [pollDraft, setPollDraft] = useState<PollDraft | null>(null);
  const [saveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [pollMaxOptions, setPollMaxOptions] = useState<number>(6);
  const [timelineValue, setTimelineValue] = useState<Date | null>(
    initialTimelineTs ? new Date(initialTimelineTs) : null
  );
  const [placeValue, setPlaceValue] = useState<PlaceRecord | null>(initialPlace);
  const draftUi = getDraftUiStrings(resolveDraftLocale());
  const isDraftDirty = useMemo(
    () =>
      (content.trim().length > 0 && content.trim() !== '<p></p>') ||
      Boolean(pollDraft?.statement?.trim()) ||
      coauthors.length > 0 ||
      Boolean(timelineValue) ||
      Boolean(placeValue),
    [coauthors.length, content, placeValue, pollDraft?.statement, timelineValue]
  );

  const { clearDraft } = useAutoSaveDraft({
    draftKey,
    value: {
      coauthors,
      content,
      placeValue,
      pollDraft,
      privacy,
      timelineTsIso: timelineValue ? timelineValue.toISOString() : null,
    },
    enabled: draftEnabled && action !== 'edit',
    isDirty: isDraftDirty,
    restoreMessage: "Restore your saved comment draft?",
    onRestore: (draft) => {
      setContent(draft.content || "");
      setPrivacy(draft.privacy || initialPrivacy || 'public');
      setPollDraft(draft.pollDraft || null);
      setCoauthors(Array.isArray(draft.coauthors) ? draft.coauthors : []);
      setTimelineValue(draft.timelineTsIso ? new Date(draft.timelineTsIso) : null);
      setPlaceValue(draft.placeValue || null);
    },
    shouldSave: (draft) =>
      Boolean(
        (draft.content.trim().length > 0 && draft.content.trim() !== '<p></p>') ||
          draft.pollDraft?.statement?.trim() ||
          draft.coauthors.length > 0 ||
          draft.timelineTsIso ||
          draft.placeValue
      ),
  });

  const internalRef = useRef<HTMLDivElement>(null);
  // Use the provided ref if available, otherwise use internal ref
  const inputRef = ref || internalRef;

  useEffect(() => {
    if (awaitingUser) {
      return;
    }
    // When the parent dictates the privacy (e.g., a principle inside a
    // solution form inherits the solution's privacy) we must not overwrite
    // it from the user's default-audience setting below.
    if (forcedPrivacy) {
      setPrivacy(forcedPrivacy);
      return;
    }
    if (currentUser && currentUser?.properties?.privacy_content_default_audience && action !== 'edit') {
      setPrivacy(currentUser?.properties?.privacy_content_default_audience.value);
    } else {
      setPrivacy(initialPrivacy || 'public');
    }
  }, [currentUser, awaitingUser, action, initialPrivacy, forcedPrivacy]);

  // Clear coauthors if privacy becomes anonymous to avoid sending collaborator requests
  useEffect(() => {
    if (privacy === 'anonymous') {
      if (coauthors.length > 0) setCoauthors([]);
      if (newCoauthors.length > 0) setNewCoauthors([]);
      // Do not mutate existingCoauthors here; they are display-only in edit mode, and server-side guard will prevent requests
    }
  }, [privacy, coauthors.length, newCoauthors.length]);

  /**
   * Fetch poll settings to enforce max options on the client.
   */
  useEffect(() => {
    const fetchPollSettings = async () => {
      try {
        const response = await AppSettingsService.getAllAppSettings({ keys: ["poll"], limit: 1 });
        const settings = Array.isArray(response) ? response : response.settings;
        const pollSetting = settings?.[0];
        const maxOptions = Number(pollSetting?.properties?.max_options);
        if (Number.isFinite(maxOptions) && maxOptions > 0) {
          setPollMaxOptions(maxOptions);
        }
      } catch (err) {
        // Use default max options on failure
      }
    };
    fetchPollSettings();
  }, []);

  // Focus editor when mounting for edit/reply actions
  useEffect(() => {
    if (autoFocus || action === 'edit' || action === 'reply' || action === 'rebut') {
      // Add a slight delay to ensure the editor is properly initialized
      setTimeout(() => {
        if (inputRef && 'current' in inputRef && inputRef.current) {
          // Try to focus the editor content
          const editor = inputRef.current.querySelector('[contenteditable=true]');
          if (editor instanceof HTMLElement) {
            editor.focus();
          }
        }
      }, 100);
    }
  }, [action, autoFocus, editorKey, inputRef]);

  // Inform parent about submit availability for external controls (e.g., modal footer)
  useEffect(() => {
    const trimmed = content.trim();
    const hasText = trimmed.length > 0 && trimmed !== '<p></p>';
    const hasPoll = Boolean(pollDraft && pollDraft.statement && pollDraft.statement.trim().length > 0);
    const canSubmit = hasText || hasPoll;
    if (typeof (onCanSubmitChange) === 'function') {
      onCanSubmitChange(canSubmit);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, pollDraft]);

  /**
   * Clear every piece of transient state the composer owns so that the next
   * compose session starts fresh.
   *
   * This is important for `action='add'` in particular: the composer stays
   * mounted across submissions, so leaving e.g. `coauthors` in place would
   * reattach the previous session's collaborators to the next comment. Edit
   * and reply callers tend to unmount on close, but resetting is still the
   * safer default.
   *
   * The function also nudges parent state mirrors (coauthor change,
   * media selection, media manager) so the parent's copy clears in lockstep
   * -- otherwise the parent might post the next comment with yesterday's
   * coauthor list.
   */
  const resetComposerState = () => {
    setContent('');
    setPollDraft(null);
    setCoauthors([]);
    setNewCoauthors([]);
    setRemovedCoauthors([]);
    setMediaFiles(null);
    setError(null);
    setTimelineValue(null);
    setPlaceValue(null);

    // Match the privacy logic used during initial mount so we don't leave
    // an edit-session privacy on the composer after it returns to add mode.
    if (currentUser?.properties?.privacy_content_default_audience && action !== 'edit') {
      setPrivacy(currentUser.properties.privacy_content_default_audience.value);
    } else {
      setPrivacy(initialPrivacy || 'public');
    }

    setEditorKey((prev) => prev + 1);

    if (typeof onCoauthorsChange === 'function') {
      onCoauthorsChange([], []);
    }
    if (typeof onMediaSelected === 'function') {
      onMediaSelected(null);
    }
    if (typeof onMediaManagerChange === 'function') {
      onMediaManagerChange([]);
    }
  };

  // When a parent uses `hideFooter` to own the submit button, expose the
  // same submit/reset/getContent surface the built-in footer uses. We
  // update the ref on every render so the parent always reaches into the
  // latest closure (content/privacy/etc.) instead of the first one.
  useEffect(() => {
    if (!submitRef) {
      return undefined;
    }
    submitRef.current = {
      submit: () => handleSubmit(),
      reset: () => resetComposerState(),
      getContent: () => content.trim(),
    };
    return () => {
      if (submitRef) {
        submitRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const finishCancel = () => {
    resetComposerState();
    // For all actions, collapse the input when cancelled.
    setIsExpanded(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleCancel = () => {
    if (draftEnabled && isDraftDirty && action !== 'edit' && onSaveDraft) {
      setSaveDraftDialogOpen(true);
      return;
    }
    finishCancel();
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setSaveDraftDialogOpen(false);
    finishCancel();
  };

  const handleSaveDraft = async () => {
    if (!onSaveDraft) {
      handleDiscardDraft();
      return;
    }
    setIsSavingDraft(true);
    try {
      await onSaveDraft(
        sanitize(content),
        privacy,
        coauthors,
        pollDraft,
        {
          place: placeValue,
          placeId: placeValue?.id || null,
          timelineTs: timelineValue ? timelineValue.toISOString() : null,
        }
      );
      clearDraft();
      setSaveDraftDialogOpen(false);
      finishCancel();
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = () => {
    const trimmedContent = content.trim();
    const hasTextContent = trimmedContent.length > 0 && trimmedContent !== '<p></p>';
    const hasPollContent = Boolean(pollDraft && pollDraft.statement && pollDraft.statement.trim().length > 0);
    if (!hasTextContent && !hasPollContent) {
      setError('Comment cannot be empty');
      return;
    }

    // Sanitize content before submitting
    const sanitizedContent = sanitize(content);
    // When editing or adding, downstream handlers may accept an extra argument (mediaFiles). We only pass the canonical 5-arg signature here, but parent wrappers can read mediaFiles from state via onMediaSelected handlers already wired in CommentsSection and ThreadForm.
    onSubmit(
      sanitizedContent,
      privacy,
      coauthors,
      newCoauthors,
      removedCoauthors,
      pollDraft,
      {
        place: placeValue,
        placeId: placeValue?.id || null,
        timelineTs: timelineValue ? timelineValue.toISOString() : null,
      }
    );

    // Submit is a commit: discard the saved draft so useAutoSaveDraft will
    // not offer to restore the content we just published.
    clearDraft();

    resetComposerState();

    // Only collapse for edit/reply actions; add flow keeps the composer open.
    if (action !== 'add') {
      setIsExpanded(false);
    }
    if (onCancel) {
      onCancel();
    }
  };

  // Determine if the component should be in fullwidth bottom position
  const isFullWidthBottom = action === 'edit' || action === 'reply' || action === 'rebut';

  // Determine the container className based on the action
  const containerClassName = isFullWidthBottom 
    ? "max-w-3xl md:ml-64 fixed bottom-0 left-0 right-0 z-50 bg-eoe-bg dark:bg-eoe-dark-bg p-3 border-t border-eoe-border dark:border-eoe-dark-border shadow-md" 
    : "mb-4 mt-2 flex flex-col gap-2";

  const getActionTitle = () => {
    if ((action === 'reply' || action === 'rebut') && commentData) {
      return (
      <>
        <p className='text-xs'>
          {action === 'rebut' ? 'Rebutting' : 'Replying to'} <a href={`/user/${commentData.username}`} className="text-eoe-text dark:text-eoe-dark-text internal-link px-1">@{commentData.username}</a> | {commentData.updated_at ? (formatRelativeTime(commentData.updated_at)) + ' ago (' + formatDate(commentData.updated_at) + ')' : formatRelativeTime(commentData?.created_at || '')}
          {action === 'rebut' && commentData.properties?.argument_type && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800">
              {commentData.properties.argument_type === 'agree' ? 'Agrees with the claim' : 'Disagrees with the claim'}
            </span>
          )}
        </p>
        <div className="p-1 rounded-md bg-eoe-icon dark:bg-eoe-dark-icon border-l-4 border-gray-300 dark:border-eoe-dark-text-heading text-base italic leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading">
          <blockquote className="text-sm leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading line-clamp-2">
            {transformHtmlContent(commentData?.content || '')}
          </blockquote>
        </div>
      </>
      );
    } else if (action === 'edit') {
      return `Editing your comment ${commentData?.updated_at ? (formatRelativeTime(commentData?.updated_at)) + ' ago (' + formatDate(commentData?.updated_at) + ')' : formatRelativeTime(commentData?.created_at || '')}`;
    }
    return null;
  };

  useEffect(() => {
    if (commentData?.id && currentUser?.id === commentData?.user_id) {
      loadCoauthorData(
        {
          id: commentData?.id,
          user_id: commentData.user_id || 0,
          properties: {
            coauthors: commentData?.properties?.coauthors || []
          }
        },
        'comment',
        currentUser?.id,
        {
          setExistingCoauthors,
          setPendingCoauthors,
          setIsLoadingCoauthors
        }
      );
    }
  }, [commentData?.id, commentData?.user_id, commentData?.properties?.coauthors, currentUser?.id]);

  const handleCoauthorSelectEdit = (suggestion: CoauthorSuggestion) => {
    selectCoauthor(
      suggestion,
      existingCoauthors,
      newCoauthors,
      setNewCoauthors,
      onCoauthorsChange,
      removedCoauthors
    );
  };

  const handleCoauthorRemoveEdit = async (coauthor: CoauthorSuggestion) => {
    await removeCoauthor(
      coauthor,
      {
        setNewCoauthors,
        setRemovedCoauthors,
        setPendingCoauthors,
        setExistingCoauthors
      },
      {
        newCoauthors,
        removedCoauthors
      },
      onCoauthorsChange
    );
  };

  const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
    if (!coauthors.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
        setCoauthors([...coauthors, suggestion]);
    }
  };

  const handleCoauthorRemove = (coauthor: CoauthorSuggestion) => {
      setCoauthors(coauthors.filter(c => !(c.id === coauthor.id && c.type === coauthor.type)));
  };

  const actionTitle = getActionTitle();
  const trimmedContent = content.trim();
  const hasTextContent = trimmedContent.length > 0 && trimmedContent !== '<p></p>';
  const hasPollStatement = Boolean(pollDraft && pollDraft.statement && pollDraft.statement.trim().length > 0);
  const canSubmit = hasTextContent || hasPollStatement;

  return (
    <div className={containerClassName} ref={inputRef}>
      <DraftSaveDialog
        isOpen={saveDraftDialogOpen}
        title={draftUi.cancelSaveTitle}
        description={draftUi.cancelSaveBody('comment')}
        discardText={draftUi.discard}
        saveText={draftUi.save}
        isSaving={isSavingDraft}
        onDiscard={handleDiscardDraft}
        onSave={() => void handleSaveDraft()}
        onDismiss={() => setSaveDraftDialogOpen(false)}
      />
      {actionTitle && (
        <div className="text-sm font-medium mb-2 text-eoe-text dark:text-eoe-dark-text">
          {actionTitle}
        </div>
      )}
      <TextEditor
        key={editorKey}
        onChange={(next: string) => {
          setContent(next);
          if (typeof onContentChange === 'function') {
            onContentChange(next);
          }
        }}
        value={content}
        editorType="comment"
        placeholder={placeholder}
        action={action === 'rebut' ? 'reply' : action}
        isEditorExpanded={isExpanded}
        initialMedia={action === 'edit' && commentData?.properties?.media_files ? commentData.properties.media_files : undefined}
        commentData={action === 'edit' ? commentData : undefined}
        pollDraft={pollDraft}
        onPollDraftChange={setPollDraft}
        pollMaxOptions={pollMaxOptions}
        onCommentMediaRequest={() => {
          // Trigger hidden file input for selecting images/videos for comments
          const picker = document.createElement('input');
          picker.type = 'file';
          picker.accept = '.jpg,.jpeg,.png,.gif,.webp,.apng,.avif,.svg,.mp4,.mpeg,.mov,.avi,.webm,.ogg,.3gp,.flv';
          picker.multiple = true;
          picker.onchange = () => {
            try {
              if (picker.files && picker.files.length > 0) {
                setMediaFiles(picker.files);
                if (typeof onMediaSelected === 'function') {
                  onMediaSelected(picker.files);
                }
              }
            } catch {}
          };
          picker.click();
        }}
        onCommentMediaManagerChange={(media: any) => {
          // Handle edit mode with complete media state
          if (action === 'edit' && media && typeof media === 'object' && 'deletedKeys' in media) {
            // Notify parent about edit media state
            if (typeof onEditMediaStateChange === 'function') {
              onEditMediaStateChange(media);
            }
            // Extract new files for upload
            if (media.newMedia && media.newMedia.length > 0) {
              try {
                const dt = new DataTransfer();
                media.newMedia.forEach((m: MediaSlot) => dt.items.add(m.file));
                setMediaFiles(dt.files);
                if (typeof onMediaSelected === 'function') {
                  onMediaSelected(dt.files);
                }
              } catch {
                if (typeof onMediaSelected === 'function') {
                  onMediaSelected(null);
                }
              }
            }
          } else {
            // Handle non-edit mode
            const mediaArray = Array.isArray(media) ? media : [];
            try {
              if (typeof onMediaManagerChange === 'function') {
                onMediaManagerChange(mediaArray);
              }
            } catch {}

            try {
              const dt = new DataTransfer();
              mediaArray.forEach((m: MediaSlot) => dt.items.add(m.file));
              setMediaFiles(dt.files);
              if (typeof onMediaSelected === 'function') {
                onMediaSelected(dt.files);
              }
            } catch {
              if (typeof onMediaSelected === 'function') {
                onMediaSelected(null);
              }
            }
          }
        }}
      >
        {effectiveShowTimelinePlace ? (
          <div className="px-3 pb-2">
            <TimelinePlaceInput
              timelineValue={effectiveShowTimeline ? timelineValue : null}
              placeValue={effectiveShowPlace ? placeValue : null}
              onTimelineChange={effectiveShowTimeline ? setTimelineValue : () => undefined}
              onPlaceChange={effectiveShowPlace ? setPlaceValue : () => undefined}
              isMobile={isMobile}
            />
          </div>
        ) : null}
        {!hideFooter && action === 'add' && (
          <div className="flex flex-row justify-between items-center w-full px-3">
            <div className="flex-shrink-0">
              {showPrivacy && !forcedPrivacy ? (
                <EntityPrivacy
                  privacy={privacy}
                  onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                  actorType="user"
                  editMode={false}
                  miniIcon={true}
                />
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {(action === 'add') && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary flex-shrink-0 rounded-full flex items-center gap-2 text-xs font-semibold hover:text-white cursor-pointer"
                >
                  {cancelText}
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`flex-shrink-0 ${canSubmit ? 'btn-primary cursor-pointer' : 'btn-primary-disabled hover:btn-primary hover:cursor-not-allowed'} rounded-full flex items-center gap-1 text-xs font-semibold hover:text-white`}
              >
                <Send className="size-3"/>
                {buttonText}
              </button>
            </div>
          </div>
        )}

        {!hideFooter && action !== 'add' && (
          <div className="flex flex-row justify-between flex-wrap w-full gap-2 px-3">
            <div className="flex-shrink-0">
              {showPrivacy && !forcedPrivacy ? (
                <EntityPrivacy
                  privacy={privacy}
                  onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                  actorType="user"
                  editMode={false}
                  miniIcon={true}
                  publicOnly={action === 'rebut' && commentData?.kind === 'argument' && commentData?.properties?.argument_type ? true : false}
                />
              ) : null}
            </div>
            <div className="flex gap-2 items-center">
              {(action === 'reply' || action === 'edit' || action === 'rebut') && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary flex-shrink-0 rounded-full flex items-center gap-2 text-xs font-semibold hover:text-white cursor-pointer"
                >
                  {cancelText}
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`flex-shrink-0 ${canSubmit ? 'btn-primary cursor-pointer' : 'btn-primary-disabled hover:btn-primary hover:cursor-not-allowed'} rounded-full flex items-center gap-1 text-xs font-semibold hover:text-white`}
              >
                <Send className="size-3"/>
                {buttonText}
              </button>
            </div>
          </div>
        )}
      </TextEditor>
      {allowCoauthors && privacy !== 'anonymous' && ((hasTextContent && action !== 'edit') || hasPollStatement || (mediaFiles && mediaFiles.length > 0)) ? (
        <div className={`${action === 'add' ? '' : 'my-2'}`}>
          <div className="flex flex-col gap-1">
              <CoauthorSelect
                inputType="add"
                placeholder="Add collaborators"
                className="w-full"
                onSelect={handleCoauthorSelect}
                excludeIds={coauthors.map(c => c.id)}
              />
              {coauthors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {coauthors.map((coauthor) => (
                    <EntityAvatar
                      key={`${coauthor.type}-${coauthor.id}`}
                      entity={coauthor as any}
                      entityType={coauthor.type}
                      entityChip={true}
                      showLink={false}
                      showCloseChip={true}
                      onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                    />
                  ))}
                </div>
              )}
          </div>
        </div>
      ) : null}
      {allowCoauthors && action === 'edit' && privacy !== 'anonymous' && (
        <DetailPanel 
          title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
          className="mt-1 mb-2 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
          defaultOpen={true}
        >
          {isLoadingCoauthors ? (
            <div className="p-2 text-center">Loading collaborators...</div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-col gap-2">
                <CoauthorSelect
                  inputType="add"
                  placeholder="Add collaborators"
                  className="w-full"
                  onSelect={handleCoauthorSelectEdit}
                />
              </div>
              {existingCoauthors.map((coauthor) => (
                <EntityAvatar
                  key={`existing-${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showCloseChip={true}
                  isInputField={false}
                  showLink={false}
                  onEntityChipClose={() => handleCoauthorRemoveEdit(coauthor)}
                />
              ))}
              {pendingCoauthors.map((coauthor) => (
                <EntityAvatar
                  key={`pending-${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showCloseChip={true}
                  isInputField={false}
                  showLink={false}
                  badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                  onEntityChipClose={() => handleCoauthorRemoveEdit(coauthor)}
                />
              ))}
              {newCoauthors.map((coauthor) => (
                <EntityAvatar
                  key={`new-${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showCloseChip={true}
                  isInputField={false}
                  showLink={false}
                  badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                  onEntityChipClose={() => handleCoauthorRemoveEdit(coauthor)}
                />
              ))}
            </div>
          )}
        </DetailPanel>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
});

export default CommentInput;